import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import EvidenceGallery from "@/components/EvidenceGallery";
import MultiMediaUpload from "@/components/MultiMediaUpload";
import VehicleEditModal from "@/components/VehicleEditModal";
import TimeClockWidget from "@/components/TimeClockWidget";
import PmcPayLookup from "@/components/PmcPayLookup";
import { isVideoPath } from "@/components/MediaViewer";
import { AlertTriangle, Footprints } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import PhotoUpload from "@/components/PhotoUpload";
import { toast } from "sonner";
import { Tag, MapPin, Camera, ClipboardCheck, Clock, Lock, LocateFixed, Map as MapIcon, Pencil, Ban, Trash2 } from "lucide-react";
import BootMap from "@/components/BootMap";
import WhatsAppButton, { bootMessage } from "@/components/WhatsAppButton";
import { fmtShort } from "@/lib/datetime";

const statusClass = { tagged: "status-tag", booted: "status-boot", paid: "status-paid", removed: "status-removed", not_booted: "status-notbooted" };

function VehicleCard({ v, onBoot, onEdit, onNotBooted, onDelete }) {
  return (
    <Card className="bg-[#131B2A] border-[#28364F] p-4 space-y-3" data-testid={`vehicle-${v.plate_number}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="plate-badge">{v.plate_number}</span>
        <Badge className={`${statusClass[v.status]} uppercase tracking-widest text-[10px]`}>{v.status}</Badge>
        {v.pmc_paid_flag && <Badge className="bg-red-500/20 text-red-300 border border-red-500/40 uppercase tracking-widest text-[10px]" data-testid={`pmc-flag-${v.plate_number}`}>PMC Paid</Badge>}
      </div>
      <div className="text-sm text-[#94A3B8] flex items-center gap-1.5">
        <MapPin className="w-3.5 h-3.5" /> {v.location}
      </div>
      {v.notes && <div className="text-xs text-[#64748B] italic">{v.notes}</div>}
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div>
          <div className="text-[#64748B] uppercase tracking-wider">Tagged</div>
          <div className="font-mono text-[#F8FAFC]">{fmtShort(v.tagged_at)}</div>
        </div>
        <div>
          <div className="text-[#64748B] uppercase tracking-wider">Booted</div>
          <div className="font-mono text-[#FCA5A5]">{fmtShort(v.booted_at)}</div>
        </div>
        <div>
          <div className="text-[#64748B] uppercase tracking-wider">Removed</div>
          <div className="font-mono text-[#6EE7B7]">{fmtShort(v.removed_at)}</div>
        </div>
      </div>
      <EvidenceGallery v={v} testId={`media-${v.plate_number}`} />
      {v.status === "not_booted" && (
        <div className="text-xs text-slate-300 rounded border border-slate-500/40 bg-slate-500/10 p-2" data-testid={`notbooted-reason-${v.plate_number}`}>
          <span className="font-bold uppercase tracking-wider text-[10px] text-slate-400">Not booted</span> — {v.not_booted_reason}
        </div>
      )}
      {v.status === "tagged" && onBoot && (
        <Button
          size="sm"
          onClick={() => onBoot(v)}
          className="w-full bg-red-500 hover:bg-red-400 text-white font-bold pulse-red"
          data-testid={`boot-btn-${v.plate_number}`}
        >
          <Lock className="w-4 h-4 mr-1" /> BOOT THIS VEHICLE
        </Button>
      )}
      {v.status === "tagged" && onNotBooted && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onNotBooted(v)}
          className="w-full bg-transparent border-slate-500/50 text-slate-300 hover:bg-slate-500/10"
          data-testid={`notbooted-btn-${v.plate_number}`}
        >
          <Ban className="w-4 h-4 mr-1" /> Not Booted (give reason)
        </Button>
      )}
      {onEdit && v.status !== "removed" && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onEdit(v)}
          className="w-full text-amber-300 hover:bg-amber-500/10"
          data-testid={`edit-btn-${v.plate_number}`}
        >
          <Pencil className="w-4 h-4 mr-1" /> Edit details
        </Button>
      )}
      {onDelete && (
        <Button size="sm" variant="ghost" onClick={() => onDelete(v)} className="w-full text-red-400 hover:bg-red-500/10" data-testid={`delete-btn-${v.plate_number}`}>
          <Trash2 className="w-4 h-4 mr-1" /> Delete
        </Button>
      )}
      {["booted", "paid"].includes(v.status) && (
        <WhatsAppButton message={bootMessage(v)} label="Send plate to removal team" className="w-full" testId={`whatsapp-btn-${v.plate_number}`} />
      )}
      {v.status === "paid" && (
        <div className="text-xs text-amber-300">Paid ${Number(v.fee_amount || 0).toFixed(2)} ({v.payment_method}) at {fmtShort(v.paid_at)} — removal in progress</div>
      )}
      {v.status === "removed" && (
        <div className="text-xs text-emerald-400">Removed by {v.removal_agent_name} — ${Number(v.fee_amount || 0).toFixed(2)} paid{v.payment_method ? ` (${v.payment_method})` : ""}</div>
      )}
    </Card>
  );
}

export default function EnforcerDashboard({ runner = false }) {
  const [tab, setTab] = useState("shift");
  const [vehicles, setVehicles] = useState([]);
  const [runnerTags, setRunnerTags] = useState([]);
  const [dup, setDup] = useState(null);
  const [plate, setPlate] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [tagPhotos, setTagPhotos] = useState([]);
  const [bootModal, setBootModal] = useState(null);
  const [bootMedia, setBootMedia] = useState([]);
  const [editVehicle, setEditVehicle] = useState(null);
  const [nbModal, setNbModal] = useState(null);
  const [nbReason, setNbReason] = useState("");
  const [nbPhotos, setNbPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [coords, setCoords] = useState(null);
  const [gpsStatus, setGpsStatus] = useState("idle");

  const grabGps = (silent) => {
    if (!navigator.geolocation) { if (!silent) toast.error("GPS not available on this device"); setGpsStatus("unavailable"); return; }
    setGpsBusy(true); setGpsStatus("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus("locked");
        if (!silent) {
          const lat = pos.coords.latitude.toFixed(5);
          const lng = pos.coords.longitude.toFixed(5);
          setLocation((prev) => prev ? `${prev} @ ${lat}, ${lng}` : `${lat}, ${lng}`);
          toast.success("GPS location added");
        }
        setGpsBusy(false);
      },
      (err) => { if (!silent) toast.error(err.message || "Could not get GPS"); setGpsStatus("failed"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const useGps = () => grabGps(false);

  useEffect(() => { if (tab === "tag" && !coords) grabGps(true); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const res = await api.get("/vehicles?scope=mine");
    setVehicles(res.data);
    if (!runner) {
      try { setRunnerTags((await api.get("/vehicles?scope=runner_tags")).data); } catch (_e) { /* ignore */ }
    }
  };

  useEffect(() => {
    const p = plate.trim();
    if (p.length < 3) { setDup(null); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/vehicles/check?plate=${encodeURIComponent(p)}`); setDup(r.data.active ? r.data : null); }
      catch (_e) { setDup(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [plate]);
  usePolling(load, 8000);
  const submitTag = async (e) => {
    e.preventDefault();
    if (!plate.trim() || !location.trim()) { toast.error("Plate and location required"); return; }
    if (dup) { toast.error(`${dup.status.toUpperCase()} already by ${dup.enforcer_name} — tag blocked`); return; }
    const photos = tagPhotos;
    if (photos.length < 3) { toast.error(`At least 3 photos/videos are required (${photos.length}/3)`); return; }
    setBusy(true);
    try {
      await api.post("/vehicles/tag", {
        plate_number: plate, location, notes: notes || null, tag_photo_paths: photos,
        lat: coords?.lat ?? null, lng: coords?.lng ?? null,
      });
      toast.success(runner ? `Tagged ${plate.toUpperCase()} — all enforcers alerted to boot` : `Tagged ${plate.toUpperCase()}`);
      setPlate(""); setLocation(""); setNotes(""); setTagPhotos([]); setCoords(null); setGpsStatus("idle"); setDup(null);
      setTab("shift");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Tag failed"); }
    finally { setBusy(false); }
  };

  const openBoot = (v) => { setBootModal(v); setBootMedia([]); };
  const openEdit = (v) => setEditVehicle(v);
  const openDelete = async (v) => {
    if (!window.confirm(`Delete ${v.plate_number}? This permanently removes the record.`)) return;
    try { await api.delete(`/vehicles/${v.id}`); toast.success(`${v.plate_number} deleted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const openNotBooted = (v) => { setNbModal(v); setNbReason(""); setNbPhotos([]); };
  const submitNotBooted = async () => {
    if (!nbModal) return;
    if (!nbReason.trim()) { toast.error("A reason is required"); return; }
    if (nbPhotos.length < 1) { toast.error("At least 1 photo is required"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${nbModal.id}/not-booted`, { reason: nbReason.trim(), photos: nbPhotos });
      toast.success(`${nbModal.plate_number} closed as Not Booted`);
      setNbModal(null); setNbReason(""); setNbPhotos([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const submitBoot = async () => {
    if (!bootModal) return;
    const photos = bootMedia.filter((p) => !isVideoPath(p));
    const videos = bootMedia.filter((p) => isVideoPath(p));
    if (photos.length < 2 || videos.length < 1) { toast.error("At least 2 photos and 1 video are required to boot"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${bootModal.id}/boot`, { boot_photo_paths: bootMedia, boot_video_path: videos[0] });
      toast.success(`${bootModal.plate_number} booted — removal team notified`);
      setBootModal(null); setBootMedia([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Boot failed"); }
    finally { setBusy(false); }
  };

  const counts = {
    tagged: vehicles.filter((v) => v.status === "tagged").length,
    booted: vehicles.filter((v) => v.status === "booted").length,
    removed: vehicles.filter((v) => v.status === "removed").length,
  };

  return (
    <AppShell>
      <div className="mb-5">
        <h1 className="font-display text-3xl font-black tracking-tight">{runner ? "Plate Runner Console" : "Enforcer Console"}</h1>
        <p className="text-sm text-[#94A3B8]">{runner ? "Tag vehicles — every enforcer is alerted to go boot" : "Your shift log"} — {new Date().toDateString()}</p>
      </div>
      {runner && <div className="mb-5"><TimeClockWidget /></div>}
      {!runner && runnerTags.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-2 text-amber-200 text-sm pulse-red" data-testid="runner-tag-banner">
          <Footprints className="w-4 h-4 shrink-0" /> {runnerTags.length} new plate-runner tag{runnerTags.length > 1 ? "s" : ""} waiting to boot — scroll to "Runner tags" below
        </div>
      )}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-blue-300">Tagged</div>
          <div className="text-2xl font-black font-display" data-testid="stat-tagged">{counts.tagged}</div>
        </Card>
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-red-300">Booted</div>
          <div className="text-2xl font-black font-display" data-testid="stat-booted">{counts.booted}</div>
        </Card>
        <Card className="bg-[#131B2A] border-[#28364F] p-3">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Removed</div>
          <div className="text-2xl font-black font-display" data-testid="stat-removed">{counts.removed}</div>
        </Card>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#131B2A] border border-[#28364F] w-full grid grid-cols-3 h-12">
          <TabsTrigger value="shift" data-testid="tab-shift" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <ClipboardCheck className="w-4 h-4 mr-1.5" /> My Shift
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="tab-map" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <MapIcon className="w-4 h-4 mr-1.5" /> Map
          </TabsTrigger>
          <TabsTrigger value="tag" data-testid="tab-tag" className="data-[state=active]:bg-cyan-500 data-[state=active]:text-[#0B0F17]">
            <Camera className="w-4 h-4 mr-1.5" /> Tag Vehicle
          </TabsTrigger>
        </TabsList>
        <TabsContent value="shift" className="mt-4 space-y-4">
          {!runner && runnerTags.length > 0 && (
            <Card className="bg-amber-500/10 border-amber-500/40 p-4 space-y-3" data-testid="runner-tags-card">
              <div className="font-display font-bold flex items-center gap-2 text-amber-200"><Footprints className="w-4 h-4" /> Runner tags waiting for a boot ({runnerTags.length})</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {runnerTags.map((v) => <VehicleCard key={v.id} v={v} onBoot={openBoot} onEdit={openEdit} onNotBooted={openNotBooted} onDelete={openDelete} />)}
              </div>
            </Card>
          )}
          <PmcPayLookup />
          {vehicles.length === 0 ? (
            <Card className="bg-[#131B2A] border-[#28364F] p-8 text-center text-[#94A3B8]">
              <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No vehicles logged today. Tap "Tag Vehicle" to start your shift.
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {vehicles.map((v) => <VehicleCard key={v.id} v={v} onBoot={runner ? null : openBoot} onEdit={openEdit} onNotBooted={runner ? null : openNotBooted} onDelete={openDelete} />)}
            </div>
          )}
        </TabsContent>
        <TabsContent value="map" className="mt-4">
          <Card className="bg-[#131B2A] border-[#28364F] p-4 space-y-3">
            <div>
              <div className="font-display font-bold flex items-center gap-2"><MapIcon className="w-4 h-4 text-cyan-400" /> My Shift Map</div>
              <p className="text-xs text-[#94A3B8]">Every vehicle you tagged or booted today, color-coded by status.</p>
            </div>
            <BootMap vehicles={vehicles} showLegend testId="enforcer-map"
              emptyLabel="Nothing logged yet today — tag a vehicle to see it here."
              noGpsLabel="Your vehicles have no GPS coordinates yet — tap “Use GPS” when tagging to pin them." />
          </Card>
        </TabsContent>
        <TabsContent value="tag" className="mt-4">
          <Card className="bg-[#131B2A] border-[#28364F] p-5">
            <form onSubmit={submitTag} className="space-y-4">
              <div>
                <Label>License plate</Label>
                <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  placeholder="ABC-1234"
                  className="bg-[#0B0F17] border-[#28364F] text-white plate uppercase text-lg tracking-widest"
                  data-testid="tag-plate-input" />
                {dup && (
                  <div className="mt-2 rounded-md border border-red-500/50 bg-red-500/15 text-red-200 text-sm p-3 flex items-start gap-2" data-testid="duplicate-plate-alert">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span><b>{plate.trim().toUpperCase()}</b> is already <b>{dup.status.toUpperCase()}</b> by {(["booted", "paid"].includes(dup.status) && dup.booter_name) ? dup.booter_name : dup.enforcer_name} at {fmtShort(dup.booted_at || dup.tagged_at)} ({dup.location}). Tagging is blocked to prevent a void.</span>
                  </div>
                )}
              </div>
              <div>
                <Label>Location</Label>
                <div className="flex gap-2">
                  <Input value={location} onChange={(e) => setLocation(e.target.value)}
                    placeholder="Lot B, row 3 near entrance"
                    className="bg-[#0B0F17] border-[#28364F] text-white"
                    data-testid="tag-location-input" />
                  <Button type="button" variant="outline"
                    onClick={useGps} disabled={gpsBusy}
                    className="bg-[#0B0F17] border-[#28364F] text-cyan-400 hover:bg-[#1C263B] hover:text-cyan-300 shrink-0"
                    data-testid="tag-gps-btn">
                    <LocateFixed className={`w-4 h-4 ${gpsBusy ? "animate-spin" : ""}`} />
                    <span className="ml-1 hidden sm:inline">{gpsBusy ? "Locating..." : "Use GPS"}</span>
                  </Button>
                </div>
                <div className="text-[11px] mt-1.5 flex items-center gap-1.5" data-testid="gps-status">
                  {gpsStatus === "locked" && coords && (
                    <span className="text-emerald-400 inline-flex items-center gap-1"><LocateFixed className="w-3 h-3" /> GPS locked · {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} — will pin on map</span>
                  )}
                  {gpsStatus === "locating" && <span className="text-cyan-400">Locating your position…</span>}
                  {(gpsStatus === "failed" || gpsStatus === "unavailable") && (
                    <span className="text-amber-400">GPS unavailable — allow location or tap Use GPS to retry</span>
                  )}
                </div>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Expired permit, blocked hydrant, etc."
                  className="bg-[#0B0F17] border-[#28364F] text-white"
                  data-testid="tag-notes-input" />
              </div>
              <div>
                <Label>Photos / videos (at least 3)</Label>
                <MultiMediaUpload value={tagPhotos} onChange={setTagPhotos} min={3} testId="tag-photo" />
              </div>
              <Button type="submit" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-400 text-[#0B0F17] font-bold h-11" data-testid="tag-submit-btn">
                <Tag className="w-4 h-4 mr-1.5" /> {busy ? "Logging..." : "Log Tag & Timestamp"}
              </Button>
            </form>
          </Card>
        </TabsContent>
      </Tabs>

      {bootModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="boot-modal">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 max-w-md w-full space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-widest text-red-300">Confirm Boot</div>
              <div className="mt-1"><span className="plate-badge">{bootModal.plate_number}</span></div>
              <div className="text-sm text-[#94A3B8] mt-2">{bootModal.location}</div>
            </div>
            <div>
              <Label>Boot evidence (at least 2 photos + 1 video)</Label>
              <MultiMediaUpload value={bootMedia} onChange={setBootMedia} minPhotos={2} minVideos={1} testId="boot-photo" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 bg-transparent border-[#28364F] text-white hover:bg-[#1C263B]"
                onClick={() => setBootModal(null)} data-testid="boot-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-red-500 hover:bg-red-400 text-white font-bold" onClick={submitBoot} disabled={busy} data-testid="boot-confirm-btn">
                {busy ? "Booting..." : "Confirm Boot"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {nbModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="notbooted-modal">
          <Card className="bg-[#131B2A] border-[#28364F] p-5 max-w-md w-full space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-300">Close without boot</div>
              <div className="mt-1"><span className="plate-badge">{nbModal.plate_number}</span></div>
              <div className="text-sm text-[#94A3B8] mt-2">{nbModal.location}</div>
            </div>
            <div>
              <Label>Why wasn't it booted?</Label>
              <Textarea value={nbReason} onChange={(e) => setNbReason(e.target.value)}
                placeholder="Vehicle drove off, valid permit shown, wrong plate, etc."
                className="bg-[#0B0F17] border-[#28364F] text-white" data-testid="notbooted-reason-input" />
            </div>
            <div>
              <Label>Proof photo (at least 1)</Label>
              <MultiMediaUpload value={nbPhotos} onChange={setNbPhotos} min={1} testId="notbooted-photo" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 bg-transparent border-[#28364F] text-white hover:bg-[#1C263B]"
                onClick={() => setNbModal(null)} data-testid="notbooted-cancel-btn">Cancel</Button>
              <Button className="flex-1 bg-slate-500 hover:bg-slate-400 text-white font-bold" onClick={submitNotBooted} disabled={busy} data-testid="notbooted-confirm-btn">
                {busy ? "Saving..." : "Mark Not Booted"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {editVehicle && (
        <VehicleEditModal vehicle={editVehicle} onClose={() => setEditVehicle(null)} onSaved={() => load()} />
      )}
    </AppShell>
  );
}
